import {
  useGetBonusPeriod,
  useGetPreviousBonusPeriodEmployees,
  useUpdateBonusPeriod,
} from 'api-hooks/bonus';
import { KIND, Notification } from 'baseui/notification';
import { NavigationRoutes } from 'common/constants';
import LoadingComponent from 'components/common/loading';
import ErrorViewComponent from 'components/elements/error-view-component';
import FetchWrapperComponent from 'components/elements/fetch-wrapper-component';
import useDismiss from 'hooks/use-dismiss';
import { DetailContainer, SeparatorVertical } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import React from 'react';
import { NavigationProps } from 'types';

import BonusPeriodForm from './components/form';
import { BonusPeriodFormType } from './components/form-type';

export type BonusPeriodViewParams = {
  id: string;
};

export default function BonusPeriodView(
  props: NavigationProps<NavigationRoutes.BonusPeriodView>,
) {
  const { mutateAsync, error } = useUpdateBonusPeriod();
  const { query: params } = useRouter();
  const id = params?.id as string;
  const query = useGetBonusPeriod({ id });
  const onDismiss = useDismiss('bonus-periods', id);
  const onSubmit = React.useCallback(
    async (values: BonusPeriodFormType) => {
      const result = await mutateAsync({
        id,
        body: values,
      });
      onDismiss();
      return result.data;
    },
    [id, mutateAsync, onDismiss],
  );

  const bonusPeriod = query.data?.data;

  const { data: previousEmployees, isFetching } =
    useGetPreviousBonusPeriodEmployees({
      params: {
        bonus_period_id: id,
      },
    });

  if (isFetching) return null;

  return (
    <FetchWrapperComponent
      onRetry={query.refetch}
      loadingComponent={<LoadingComponent />}
      errorComponent={
        <DetailContainer>
          <ErrorViewComponent refetch={query.refetch} />
        </DetailContainer>
      }
      isLoading={query.isFetching}
      component={
        <>
          {!!error?.message && (
            <>
              <Notification
                overrides={{
                  Body: { style: { width: 'auto', wordBreak: 'break-all' } },
                }}
                kind={KIND.negative}
                closeable
              >
                {() => error.message}
              </Notification>
              <SeparatorVertical $margin={8} />
            </>
          )}
          <BonusPeriodForm
            onSubmit={onSubmit}
            bonusPeriod={bonusPeriod}
            previousEmployees={previousEmployees?.data}
            {...props}
          />
        </>
      }
    />
  );
}
